import { Children, PropsWithChildren, ReactNode, useRef } from 'react';

import { motion } from 'motion/react';

import { ActionButton } from '@ui';

import * as css from './SwingCardList.css';
import { useSlider, useSliderSwipe } from './useSlider';

export const SwingListHeader = ({ children }: PropsWithChildren) => {
    return <h4 className={css.cardListHeader}>{children}</h4>;
};

export type SwingCardListProps = {
    title: string;
    variant?: 'horizontal' | 'vertical';
    emptyListNode?: ReactNode;
    fullHeight?: boolean;
};

export const SwingCardList = ({
    children,
    variant,
    emptyListNode,
    title,
    fullHeight = false,
}: PropsWithChildren<SwingCardListProps>): ReactNode => {
    const count = Children.count(children);

    return (
        <div className={css.root({ fullHeight })}>
            <SwingListHeader>{title}</SwingListHeader>
            {count === 0 ? (
                <div>{emptyListNode}</div>
            ) : (
                <div className={css.cardList({ variant })}>
                    {Children.map(children, (child) => {
                        return <div className={css.cardListItem({ variant })}>{child}</div>;
                    })}
                </div>
            )}
        </div>
    );
};

export type SwingCardGridProps = {
    title?: string;
    emptyListNode?: ReactNode;
    pagination?: ReactNode;
    rowCount?: 2 | 3;
    fullHeight?: boolean;
};

export const SwingCardGrid = ({
    children,
    emptyListNode,
    title,
    pagination,
    rowCount,
    fullHeight = true,
}: PropsWithChildren<SwingCardGridProps>): ReactNode => {
    const isGridEmpty = Children.count(children) === 0;

    return (
        <div className={css.root({ fullHeight })}>
            {title && <SwingListHeader>{title}</SwingListHeader>}
            <div className={css.cardGrid({ rowCount })}> {isGridEmpty ? <div>{emptyListNode}</div> : children}</div>
            {pagination}
        </div>
    );
};

type SwingCardGridPaginationProps = {
    prevPage: () => void;
    currentPage: number;
    totalPages?: number;
    nextPage: () => void;
};

export const SwingCardGridPagination = ({
    prevPage,
    currentPage,
    totalPages,
    nextPage,
}: SwingCardGridPaginationProps): ReactNode => {
    return (
        <div className={css.cardGridPagination}>
            <ActionButton buttonSize="small" icon="caret-left" onClick={prevPage} isDisabled={currentPage === 1} />
            <span>{totalPages ? `Page ${currentPage}/${totalPages}` : `Page ${currentPage}`}</span>
            <ActionButton
                buttonSize="small"
                icon="caret-right"
                onClick={nextPage}
                isDisabled={currentPage === totalPages}
            />
        </div>
    );
};

type CardListSliderProps = {
    title: string;
    emptyListNode?: ReactNode;
    fullHeight?: boolean;
};

export const CardListSlider = ({
    children,
    title,
    emptyListNode,
}: PropsWithChildren<CardListSliderProps>): ReactNode => {
    const count = Children.count(children);

    const containerRef = useRef<HTMLDivElement>(null);

    const { next, prev, leftOffset, showLeftButton, showRightButton } = useSlider({
        ref: containerRef,
        count,
    });

    useSliderSwipe({ ref: containerRef, next, prev });

    return (
        <div className={css.root()}>
            <SwingListHeader>{title}</SwingListHeader>

            <div className={css.spinner} ref={containerRef}>
                <motion.div
                    className={css.cardList({ variant: 'horizontal' })}
                    animate={{ left: -leftOffset, position: 'relative' }}
                >
                    {count === 0 ? <div>{emptyListNode}</div> : children}
                </motion.div>
            </div>

            {showLeftButton && (
                <div className={css.leftButton}>
                    <ActionButton buttonSize="small" icon="caret-left" onClick={prev} variant="float" />
                </div>
            )}
            {showRightButton && (
                <div className={css.rightButton}>
                    <ActionButton buttonSize="small" icon="caret-right" onClick={next} variant="float" />
                </div>
            )}
        </div>
    );
};
